/**
 * Tenants Service
 * 
 * Fetches tenant information from the API.
 */

import type { Tenant } from '../data/types';
import { apiFetch, type ApiResponse } from './api';

// ============================================================================
// API Response Types
// ============================================================================

interface TenantsResponse {
  tenants: Array<{
    tenantId: string;
    displayName: string;
    onboardingState: string;
    incidentCount: number;
  }>;
}

// ============================================================================
// GET TENANTS
// ============================================================================

/**
 * Fetches all active tenants
 */
export async function getTenants(): Promise<ApiResponse<Tenant[]>> {
  const result = await apiFetch<TenantsResponse>('/tenants');
  
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }

  const tenants: Tenant[] = result.data.tenants.map(t => ({
    tenantId: t.tenantId,
    externalTenantId: '', // Not exposed via this endpoint
    displayName: t.displayName,
    onboardingState: t.onboardingState as Tenant['onboardingState'],
    incidentCount: t.incidentCount,
  }));

  return { data: tenants, success: true };
}

/**
 * Fetches a single tenant by ID
 */
export async function getTenantById(tenantId: string): Promise<ApiResponse<Tenant | null>> {
  const result = await getTenants();
  
  if (!result.success) {
    return { data: null, success: false, error: result.error };
  }

  const tenant = result.data.find(t => t.tenantId === tenantId);
  return { data: tenant || null, success: true };
}
